import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../lib/auth.js';
import { api, ApiError, fetchCompanyOptions } from '../lib/api.js';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useFormFields } from '../hooks/useFormFields.js';
import { getEquipmentRule, calculateTestLoad, buildPoint5Sentence, buildEquipmentTypeOptions } from '@gng-ehs/shared/equipment-rules';
import { Pagination } from '../components/Pagination';
import { useDebounce } from '../hooks/useDebounce';
import { draftManager } from '../lib/draftManager.js';
import { useDraftAutoSave } from '../hooks/useDraftAutoSave.js';
import { DraftRecoveryBanner } from '../components/DraftRecoveryBanner.js';
import { SectionDraftsView } from '../components/SectionDraftsView.js';
import type { UserDraft } from '@gng-ehs/shared';
import { TableLoader } from '../components/TableLoader';
import { CompanyHoverBadge } from '../components/CompanyHoverBadge';
import { ShortCodeTag } from '../components/ShortCodeTag';
import { SortableHeader } from '../components/SortableHeader';
import { TimestampCell } from '../components/TimestampCell';
import { formatDateOnly } from '../lib/dateUtils';
import { AsyncSearchableSelect } from '../components/AsyncSearchableSelect.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { BulkEquipmentModal } from '../components/BulkEquipmentModal.js';
import { InlineEquipmentTypeForm } from '../components/InlineEquipmentTypeForm.js';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/BulkActionBar';

interface Equipment {
  id: string;
  company_id: string;
  company_name?: string;
  company_short_code?: string;
  equipment_name_type: string;
  equipment_short_code: string;
  internal_id_number: string;
  swl_value?: number;
  lift_height?: string;
  make?: string;
  model?: string;
  first_use_type: string;
  first_use_value: string;
  created_at?: string;
  updated_at?: string;
}

const yearOptions = Array.from({ length: 60 }, (_, i) => String(new Date().getFullYear() - i));

export function EquipmentPage() {
  const { canWrite } = useAuth();
  const isWrite = canWrite('equipment');

  const [items, setItems] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'created_at' || field === 'updated_at' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [showInlineTypeForm, setShowInlineTypeForm] = useState(false);
  const [customTypes, setCustomTypes] = useState<Array<{ key: string; label: string; short_code?: string }>>([]);
  const [editing, setEditing] = useState<Equipment | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const {
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    selectedCount,
    masterCheckboxRef,
  } = useBulkSelection(items);

  const [detailEquipment, setDetailEquipment] = useState<Equipment | null>(null);

  const fields = useFormFields({
    company_id: '', equipment_name_type: '', equipment_short_code: '',
    swl_value: '', lift_height: '', make: '', model: '',
    equipment_size: '', location_in_factory: '',
    first_use_type: 'NOT_APPLICABLE', first_use_value: '',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'records' | 'drafts'>('records');
  const [draftCount, setDraftCount] = useState<number>(() => draftManager.listDrafts('equipment').length);
  const [isResumed, setIsResumed] = useState(false);
  const handledResumeRef = useRef<string | null>(null);

  useEffect(() => {
    const update = () => setDraftCount(draftManager.listDrafts('equipment').length);
    update();
    const unsub = draftManager.subscribe(update);
    return unsub;
  }, []);

  const restoreEquipmentDraft = useCallback((draft: UserDraft) => {
    const data = draft.form_data as Record<string, unknown>;
    setEditing(null);
    if (data && typeof data === 'object') {
      fields.patchValues(data as any);
    }
    setModalOpen(true);
  }, []);

  const handleResumeDraft = (draft: UserDraft) => {
    setIsResumed(true);
    restoreEquipmentDraft(draft);
  };

  // Check URL search params for ?resumeDraft=...
  useEffect(() => {
    const resumeId = searchParams.get('resumeDraft');
    if (resumeId && handledResumeRef.current !== resumeId) {
      handledResumeRef.current = resumeId;
      const draft = draftManager.getDraft(resumeId);
      if (draft && draft.entity_type === 'equipment') {
        setIsResumed(true);
        restoreEquipmentDraft(draft);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('resumeDraft');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, restoreEquipmentDraft]);

  const isEquipmentDirty = useMemo(() => {
    if (!modalOpen || editing) return false;
    return Boolean(
      fields.values.company_id ||
      fields.values.equipment_name_type ||
      fields.values.equipment_short_code ||
      fields.values.swl_value ||
      fields.values.make ||
      fields.values.model
    );
  }, [modalOpen, editing, fields.values]);

  const draftTitle = useMemo(() => {
    const code = fields.values.equipment_short_code ? `Tag: ${fields.values.equipment_short_code}` : '';
    const type = fields.values.equipment_name_type || 'Equipment';
    return code ? `${type} (${code})` : `New ${type}`;
  }, [fields.values.equipment_short_code, fields.values.equipment_name_type]);

  const { existingDraft, restoreDraft, discardDraft, clearDraft } = useDraftAutoSave({
    entityType: 'equipment',
    draftId: 'dft_equipment_new',
    enabled: modalOpen && !editing,
    isDirty: isEquipmentDirty,
    isResumed,
    formData: fields.values,
    title: draftTitle,
    subtitle: fields.values.make ? `Make: ${fields.values.make}` : undefined,
  });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.equipment.list({ search, company_id: companyFilter, equipment_type: typeFilter, page, limit: 20, sortBy, sortOrder }),
      api.equipmentTypes.list(),
    ])
      .then(([eq, types]) => {
        setItems(eq.data as Equipment[] ?? []);
        setTotalPages((eq as unknown as Record<string, number>).totalPages ?? 1);
        setTotal((eq as unknown as Record<string, number>).total ?? 0);
        if (types.data) setCustomTypes(types.data.map((t) => ({ key: t.key, label: t.label, short_code: t.short_code })));
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, companyFilter, typeFilter, sortBy, sortOrder]);

  // Auto-search when user types (debounced)
  useDebounce(search, () => { setPage(1); load(); });

  const allTypeOptions = useMemo(() => {
    return buildEquipmentTypeOptions(customTypes);
  }, [customTypes]);

  const rule = useMemo(
    () => getEquipmentRule(fields.values.equipment_name_type),
    [fields.values.equipment_name_type],
  );

  const [tagStatus, setTagStatus] = useState<{ loading: boolean; available?: boolean; message?: string; suggested?: string }>({ loading: false });

  // Auto-suggest Tag Name when company_id or equipment_name_type changes for new equipment
  useEffect(() => {
    if (editing) return;
    const { company_id, equipment_name_type } = fields.values;
    if (!company_id || !equipment_name_type) return;

    api.equipment.suggestTagName(company_id, equipment_name_type)
      .then((res) => {
        if (res.data?.suggested) {
          fields.setField('equipment_short_code', res.data.suggested);
        }
      })
      .catch(() => { /* ignore */ });
  }, [fields.values.company_id, fields.values.equipment_name_type, editing]);

  // Explicit Suggest Tag Name button click handler
  const handleSuggestTagName = async () => {
    const { company_id, equipment_name_type } = fields.values;
    if (!company_id) {
      setError('Please select a Company first.');
      setTagStatus({ loading: false });
      return;
    }
    if (!equipment_name_type) {
      setError('Please select an Equipment Type first.');
      setTagStatus({ loading: false });
      return;
    }
    setTagStatus({ loading: true });
    try {
      const res = await api.equipment.suggestTagName(company_id, equipment_name_type);
      if (res.data?.suggested) {
        fields.setField('equipment_short_code', res.data.suggested);
      }
    } catch {
      /* ignore */
    } finally {
      setTagStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  // Live Tag Name uniqueness validation
  const debouncedTagName = fields.values.equipment_short_code;
  useDebounce(debouncedTagName, () => {
    const { company_id, equipment_short_code } = fields.values;
    if (!company_id || !equipment_short_code.trim()) {
      setTagStatus({ loading: false });
      return;
    }
    setTagStatus({ loading: true });
    api.equipment.checkTagName(company_id, equipment_short_code.trim(), editing?.id)
      .then((res) => {
        if (res.data) {
          setTagStatus({
            loading: false,
            available: res.data.available,
            message: res.data.message,
            suggested: res.data.suggested,
          });
        }
      })
      .catch(() => setTagStatus({ loading: false }))
      .finally(() => {
        setTagStatus((prev) => ({ ...prev, loading: false }));
      });
  });

  const previewLoad = useMemo(() => {
    if (!rule) return null;
    const swl = parseFloat(fields.values.swl_value) || null;
    return calculateTestLoad(rule, swl);
  }, [rule, fields.values.swl_value]);

  const previewSentence = useMemo(() => {
    if (!rule) return '';
    const swl = parseFloat(fields.values.swl_value) || null;
    return buildPoint5Sentence(rule, swl, 'DD-MM-YYYY', '[Inspector]');
  }, [rule, fields.values.swl_value]);

  const openCreate = () => {
    setIsResumed(false);
    setActiveTab('records');
    setEditing(null);
    setTagStatus({ loading: false });
    fields.reset({
      company_id: '', equipment_name_type: '', equipment_short_code: '',
      swl_value: '', lift_height: '', make: '', model: '',
      equipment_size: '', location_in_factory: '',
      first_use_type: 'NOT_APPLICABLE', first_use_value: '',
    });
    setModalOpen(true);
  };

  const openEdit = (eq: Equipment) => {
    setIsResumed(false);
    setEditing(eq);
    setTagStatus({ loading: false });
    fields.patchValues({
      company_id: eq.company_id,
      equipment_name_type: eq.equipment_name_type,
      equipment_short_code: eq.equipment_short_code,
      swl_value: eq.swl_value?.toString() ?? '',
      lift_height: eq.lift_height ?? '',
      make: eq.make ?? '',
      model: eq.model ?? '',
      equipment_size: '',
      location_in_factory: '',
      first_use_type: eq.first_use_type,
      first_use_value: eq.first_use_value,
    });
    setModalOpen(true);
  };

  const isEquipmentFormValid = useMemo(() => {
    const v = fields.values;
    return (
      !!v.company_id &&
      !!v.equipment_name_type?.trim() &&
      !!v.equipment_short_code?.trim() &&
      (tagStatus.available !== false)
    );
  }, [fields.values, tagStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fields.values.company_id) {
      setError('Please select a Company.');
      return;
    }
    setSaving(true);
    let finalTagName = fields.values.equipment_short_code.trim().toUpperCase();
    if (!finalTagName) {
      setError('Tag Name is required. Click "Suggest" to auto-generate a unique Tag Name.');
      setSaving(false);
      return;
    }

    if (tagStatus.available === false) {
      setError(tagStatus.message || 'Tag Name is already taken for this company.');
      setSaving(false);
      return;
    }
    const payload = {
      ...fields.values,
      equipment_short_code: finalTagName,
      swl_value: fields.values.swl_value ? parseFloat(fields.values.swl_value) : undefined,
    };
    try {
      if (editing) await api.equipment.update(editing.id, payload);
      else await api.equipment.create(payload);
      clearDraft();
      setModalOpen(false);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!deleteId) return;
    try { await api.equipment.delete(deleteId); load(); }
    catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to delete'); }
    try {
      await api.equipment.delete(deleteId);
      setSuccess('Equipment moved to Trash.');
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    setError('');
    try {
      const res = await api.equipment.bulkDelete(Array.from(selectedIds));
      setSuccess(res.data?.message || `Moved ${res.data?.count ?? selectedIds.size} equipment to Trash.`);
      clearSelection();
      setBulkDeleteDialogOpen(false);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete selected equipment');
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-hidden pt-1 px-1">
      {/* Action Toolbar */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Tab Pill Toggle */}
          <div className="flex items-center p-0.5 bg-gray-200/80 rounded-lg border border-gray-300/70">
            <button
              type="button"
              onClick={() => setActiveTab('records')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                activeTab === 'records'
                  ? 'bg-white text-gray-900 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Equipment Records
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('drafts')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'drafts'
                  ? 'bg-white text-amber-900 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span>Drafts</span>
              {draftCount > 0 && (
                <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full border border-amber-200">
                  {draftCount}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'records' && (
            <button onClick={load} className="btn btn-secondary">Refresh</button>
          )}
        </div>
        {isWrite && (
          <div className="flex gap-2 items-center shrink-0">
            <button onClick={openCreate} className="btn btn-primary">+ Add Equipment</button>
          </div>
        )}
      </div>

      {/* Search & Filters Bar */}
      {activeTab === 'records' && (
        <div className="shrink-0 bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-2 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-2 items-center w-full sm:w-auto flex-1 min-w-0">
            <div className="relative w-full sm:w-72">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search equipment ID, tag name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-9 text-sm w-full"
              />
            </div>
            <div className="w-full sm:w-64">
              <AsyncSearchableSelect
                placeholder="Filter by Company..."
                fetchOptions={fetchCompanyOptions}
                value={companyFilter}
                onChange={(val) => setCompanyFilter(val)}
              />
            </div>
            <div className="w-full sm:w-64">
              <SearchableSelect
                placeholder="Filter by Equipment Type..."
                options={allTypeOptions}
                value={typeFilter}
                onChange={(val) => setTypeFilter(val)}
              />
            </div>
          </div>
          {(search || companyFilter || typeFilter) && (
            <button
              onClick={() => { setSearch(''); setCompanyFilter(''); setTypeFilter(''); }}
              className="text-xs text-brand-700 font-semibold hover:underline shrink-0 px-2 py-1 cursor-pointer"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {success && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs font-semibold text-emerald-800 flex items-center justify-between shadow-xs">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-emerald-600 hover:text-emerald-900 ml-2 font-bold cursor-pointer">×</button>
        </div>
      )}
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {activeTab === 'drafts' ? (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <SectionDraftsView
            entityType="equipment"
            sectionTitle="Equipment"
            onResume={handleResumeDraft}
          />
        </div>
      ) : loading ? (
        <TableLoader />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-2">
          <div className="table-wrapper">
            <table className="table">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  <th className="w-10 px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      ref={masterCheckboxRef}
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      title="Select all"
                    />
                  </th>
                  <SortableHeader label="ID Number" field="internal_id_number" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Company" field="company_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Tag Name" field="equipment_short_code" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th>SWL</th>
                  <th>Make / Model</th>
                  <SortableHeader label="Created / Updated" field="created_at" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-gray-500 py-8">No equipment found</td></tr>
                ) : items.map((eq) => (
                  <tr key={eq.id} className={isSelected(eq.id) ? 'bg-brand-50/50 hover:bg-brand-50/70' : undefined}>
                    <td className="w-10 px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected(eq.id)}
                        onChange={() => {}}
                        onClick={(e) => toggleSelect(eq.id, e.shiftKey)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                    </td>
                    <td className="font-mono text-xs font-medium text-brand-700 whitespace-nowrap">{eq.internal_id_number}</td>
                    <td className="font-medium text-sm">
                      {eq.company_name ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span className="font-medium text-gray-900">
                            {eq.company_name}
                          </span>
                          {eq.company_short_code && (
                            <CompanyHoverBadge shortCode={eq.company_short_code} companyId={eq.company_id} />
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td>
                      <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded-md font-medium text-slate-700 border border-slate-200">
                        {eq.equipment_short_code}
                      </span>
                    </td>
                    <td className="text-gray-500 text-sm">{eq.swl_value ? `${eq.swl_value} Ton` : '—'}</td>
                    <td className="text-gray-500 text-sm">{eq.make ?? '—'}{eq.model ? ` / ${eq.model}` : ''}</td>
                    <td className="whitespace-nowrap">
                      <TimestampCell createdAt={eq.created_at} updatedAt={eq.updated_at} />
                    </td>
                    <td>
                      <div className="flex gap-1.5 items-center">
                        <button
                          onClick={() => setDetailEquipment(eq)}
                          title="View Equipment Details"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/80 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>View</span>
                        </button>
                        {isWrite && (
                          <>
                            <button
                              onClick={() => openEdit(eq)}
                              title="Edit Equipment"
                              className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-800 border border-amber-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            <button
                              onClick={() => setDeleteId(eq.id)}
                              title="Delete Equipment"
                              className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="shrink-0 pt-1">
            <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
          </div>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => { setModalOpen(false); setIsResumed(false); setEditing(null); }} title={editing ? 'Edit Equipment' : 'Add Equipment'} size="xl">
        <DraftRecoveryBanner
          draft={existingDraft}
          onRestore={() => {
            const data = restoreDraft();
            if (data && existingDraft) {
              restoreEquipmentDraft({ ...existingDraft, form_data: data });
            }
          }}
          onDiscard={discardDraft}
        />

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Section 1: Company, Equipment Type & Tag Name */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              1. Company, Equipment Type & Tag Name
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Company" required>
                <AsyncSearchableSelect
                  placeholder="Search client company…"
                  fetchOptions={fetchCompanyOptions}
                  value={fields.values.company_id}
                  onChange={(val) => fields.setField('company_id', val)}
                />
              </Field>
              <Field
                label="Equipment Type"
                required
                hint={
                  <button
                    type="button"
                    onClick={() => setShowInlineTypeForm(!showInlineTypeForm)}
                    className="text-[11px] text-brand-700 font-bold hover:underline cursor-pointer"
                  >
                    + New Type
                  </button>
                }
              >
                <SearchableSelect
                  placeholder="Search equipment type…"
                  options={allTypeOptions}
                  value={fields.values.equipment_name_type}
                  onChange={(val) => {
                    fields.setField('equipment_name_type', val);
                    if (fields.values.company_id && val) {
                      api.equipment.suggestTagName(fields.values.company_id, val)
                        .then((res) => {
                          if (res.data?.suggested) {
                            fields.setField('equipment_short_code', res.data.suggested);
                          }
                        })
                        .catch(() => { /* ignore */ });
                    }
                  }}
                />
                {showInlineTypeForm && (
                  <InlineEquipmentTypeForm
                    onCreated={(newType) => {
                      setCustomTypes((prev) => [...prev, newType]);
                      fields.setField('equipment_name_type', newType.key);
                      if (fields.values.company_id && newType.key) {
                        api.equipment.suggestTagName(fields.values.company_id, newType.key)
                          .then((res) => {
                            if (res.data?.suggested) {
                              fields.setField('equipment_short_code', res.data.suggested);
                            }
                          })
                          .catch(() => { /* ignore */ });
                      }
                      setShowInlineTypeForm(false);
                    }}
                    onCancel={() => setShowInlineTypeForm(false)}
                  />
                )}
              </Field>
              <Field
                label="Tag Name"
                required
                hint={<span className="text-[11px] text-brand-600 font-medium">Click Suggest to auto-generate</span>}
              >
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <input
                      className={`input font-mono uppercase font-semibold ${
                        tagStatus.available === false ? 'border-red-500 bg-red-50 focus:ring-red-500' : ''
                      }`}
                      {...fields.bind('equipment_short_code')}
                      placeholder="e.g. BOPT-01, EOT-01"
                      required
                    />
                    <button
                      type="button"
                      onClick={handleSuggestTagName}
                      title="Suggest available unique Tag Name for this company"
                      className="px-2.5 py-1 text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200/80 rounded-lg shrink-0 cursor-pointer"
                    >
                      Suggest
                    </button>
                  </div>
                  {tagStatus.loading && (
                    <p className="text-xs text-blue-600 font-medium flex items-center gap-1.5 pt-0.5">
                      <span className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                      Checking Tag Name availability…
                    </p>
                  )}
                  {!tagStatus.loading && tagStatus.available === true && fields.values.equipment_short_code.trim() && (
                    <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1 pt-0.5">
                      ✓ Tag Name '{fields.values.equipment_short_code.toUpperCase()}' is available for this company
                    </p>
                  )}
                  {!tagStatus.loading && tagStatus.available === false && fields.values.equipment_short_code.trim() && (
                    <div className="text-xs text-red-600 font-semibold space-y-0.5 pt-0.5">
                      <p>✕ {tagStatus.message || `Tag Name '${fields.values.equipment_short_code.toUpperCase()}' already exists for this company`}</p>
                      {tagStatus.suggested && (
                        <p>
                          Suggested Tag Name:{' '}
                          <button
                            type="button"
                            onClick={() => fields.setField('equipment_short_code', tagStatus.suggested!)}
                            className="text-brand-700 underline font-bold hover:text-brand-900 cursor-pointer"
                          >
                            Use '{tagStatus.suggested}'
                          </button>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </Field>
            </div>
            {rule && (
              <div className="flex items-center gap-2 text-xs bg-white p-2.5 rounded-lg border border-slate-200">
                <span className="text-slate-500 font-medium">Equipment Type Code:</span>
                <ShortCodeTag code={rule.key} />
                <span className="text-slate-400 font-mono text-[11px] ml-auto">Rule Key: {rule.key}</span>
              </div>
            )}
          </div>

          {/* Section 2: Technical Specifications & Dimensions */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              2. Technical Specifications & Dimensions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="SWL (Safe Working Load in Tons)">
                <input className="input" type="number" step="0.1" {...fields.bind('swl_value')} placeholder="e.g. 5.0" />
              </Field>
              <Field label="Lift Height">
                <input className="input" {...fields.bind('lift_height')} placeholder="e.g. 6.0 Meters / 200 mm" />
              </Field>
              <Field label="Equipment Dimensions / Size">
                <input className="input" {...fields.bind('equipment_size')} placeholder="e.g. 560 mm (W) x 1150 mm (L)" />
              </Field>
              <Field label="Location in Factory / Department">
                <input className="input" {...fields.bind('location_in_factory')} placeholder="e.g. Bay 2 / Maintenance Shop" />
              </Field>
            </div>
          </div>

          {/* Section 3: Make, Model & First Use Details */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              3. Make, Model & First Use Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Make / Manufacturer">
                <input className="input" {...fields.bind('make')} placeholder="e.g. Godrej / Demag" />
              </Field>
              <Field label="Model Name / No.">
                <input className="input" {...fields.bind('model')} placeholder="e.g. IND-5000" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Date First Taken Into Use Status">
                <select className="input" {...fields.bind('first_use_type')} onChange={(e) => {
                  fields.bind('first_use_type').onChange(e);
                  const val = e.target.value;
                  if (val === 'EXACT_DATE') {
                    fields.setField('first_use_value', new Date().toISOString().split('T')[0]);
                  } else if (val === 'YEAR_ONLY') {
                    fields.setField('first_use_value', String(new Date().getFullYear()));
                  } else {
                    fields.setField('first_use_value', '');
                  }
                }}>
                  <option value="NOT_APPLICABLE">Not Applicable</option>
                  <option value="NOT_KNOWN">Not Known</option>
                  <option value="EXACT_DATE">Exact Date (DD/MM/YYYY)</option>
                  <option value="YEAR_ONLY">Year Only (YYYY)</option>
                </select>
              </Field>
              {fields.values.first_use_type === 'EXACT_DATE' && (
                <Field label="First Use Date">
                  <input className="input" type="date" {...fields.bind('first_use_value')} />
                </Field>
              )}
              {fields.values.first_use_type === 'YEAR_ONLY' && (
                <Field label="First Use Year">
                  <select className="input" {...fields.bind('first_use_value')}>
                    <option value="">Select Year</option>
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </Field>
              )}
            </div>
          </div>

          {/* Section 4: Point 5 Certificate Live Preview */}
          {rule && (
            <div className="rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Form 10 Point 5 Certificate Sentence Live Preview
                </p>
                {previewLoad && (
                  <span className="px-2 py-0.5 rounded-full bg-blue-600 text-white font-mono text-[11px] font-bold">
                    Test Load: {previewLoad}
                  </span>
                )}
              </div>
              <p className="text-xs text-blue-950 leading-relaxed font-sans bg-white/80 p-3 rounded-lg border border-blue-100/80">
                {previewSentence}
              </p>
            </div>
          )}

          {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

          <div className="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button type="button" onClick={() => { setModalOpen(false); setIsResumed(false); }} className="btn btn-secondary text-sm h-9 whitespace-nowrap shrink-0">Cancel</button>
            <button type="submit" disabled={saving || !isEquipmentFormValid} className="btn btn-primary text-sm h-9 px-5 whitespace-nowrap shrink-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Saving Equipment…' : 'Save Equipment'}
            </button>
          </div>
        </form>
      </Modal>

      {detailEquipment && (
        <EquipmentDetail equipment={detailEquipment} onClose={() => setDetailEquipment(null)} />
      )}

      {bulkModalOpen && (
        <BulkEquipmentModal
          open={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          onSuccess={(_count) => {
            load();
          }}
          initialCompanyId={companyFilter}
        />
      )}

      <ConfirmDialog
        open={!!deleteId}
        title="Move Equipment to Trash"
        message="Are you sure you want to delete this equipment? It will be moved to Trash and can be restored within 30 days."
        confirmLabel="Move to Trash"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      {isWrite && (
        <BulkActionBar
          selectedCount={selectedCount}
          entityName="equipment"
          onClearSelection={clearSelection}
          onDelete={() => setBulkDeleteDialogOpen(true)}
          isDeleting={bulkDeleting}
        />
      )}

      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title={`Move ${selectedCount} Equipment to Trash`}
        message={`Are you sure you want to delete ${selectedCount} selected equipment? They will be moved to Trash and can be restored within 30 days.`}
        confirmLabel={`Move ${selectedCount} to Trash`}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />

    </div>
  );
}

function EquipmentDetail({ equipment, onClose }: { equipment: Equipment; onClose: () => void }) {
  return (
    <Modal open={true} onClose={onClose} title={`${equipment.internal_id_number} - ${equipment.equipment_name_type}`} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DetailRow label="Internal ID No." value={equipment.internal_id_number} />
          <DetailRow label="Company" value={equipment.company_name} />
          <DetailRow label="Equipment Type" value={equipment.equipment_name_type} />
          <DetailRow label="Tag Name" value={equipment.equipment_short_code} />
          <DetailRow label="SWL Capacity" value={equipment.swl_value ? `${equipment.swl_value} Ton` : '—'} />
          <DetailRow label="Lift Height" value={equipment.lift_height} />
          <DetailRow label="Make" value={equipment.make} />
          <DetailRow label="Model" value={equipment.model} />
          <DetailRow label="First Use Type" value={equipment.first_use_type} />
          <DetailRow label="First Use Value" value={equipment.first_use_type === 'EXACT_DATE' ? formatDateOnly(equipment.first_use_value) : (equipment.first_use_value || '—')} />
        </div>
      </div>
    </Modal>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value || '—'}</p>
    </div>
  );
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="label mb-0">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
        {hint}
      </div>
      {children}
    </div>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
      <span>{message}</span>
      <button onClick={onDismiss} className="text-red-500 hover:text-red-700">✕</button>
    </div>
  );
}
